// Notification sound - default WhatsApp-like sound file. An account owner can
// replace this account-wide (Settings -> Notifications, see notificationSlice.js's
// soundUrl); every team member on that account then hears the uploaded file
// instead. null/missing soundUrl falls back to this default.
const NOTIFICATION_SOUND = '/sounds/Notification.mp3';

let audioElement = null;
// The src audioElement was actually built with, so a changed account sound
// can be detected and the element swapped -- without this, playNotificationSound
// kept reusing whatever Audio instance was created first, so an account-wide
// sound change only took effect after a full page reload.
let audioElementSrc = null;
let initAttempted = false;

// Store is read off window (see redux/store.js) instead of a static import.
// socketSharedState.js documents the same workaround for the same reason:
// store.js -> authSlice.js -> socketSharedState.js already forms an import
// cycle, and this module is reached from that same early socket/message-
// handling path, not just from a deferred click handler.
const resolveSoundSrc = () => {
  try {
    const store = typeof window !== 'undefined' ? window.store : undefined;
    const soundUrl = store?.getState?.()?.notifications?.settings?.soundUrl;
    return soundUrl || NOTIFICATION_SOUND;
  } catch (_) {
    return NOTIFICATION_SOUND;
  }
};

// Creates the shared Audio element if missing, or swaps it for a new one if the
// account's chosen sound has changed since it was created.
const ensureAudioElement = () => {
  const desiredSrc = resolveSoundSrc();
  if (audioElement && audioElementSrc === desiredSrc) return;
  try {
    audioElement = new Audio(desiredSrc);
    audioElement.preload = 'auto';
    audioElement.volume = 1.0; // Set volume to 100%
    audioElement.load();
    audioElementSrc = desiredSrc;
  } catch (error) {
    console.warn("Could not initialize notification sound:", error);
  }
};

// Initialize audio early (required for autoplay policies)
export const initializeNotificationAudio = () => {
  if (initAttempted) return; // Only attempt once
  initAttempted = true;

  try {
    // Audio is ready, but we don't play it here.
    // The actual play() will happen when a message arrives
    // Browser autoplay policies will handle the first play attempt
    ensureAudioElement();
    console.log('✅ [NOTIFICATION] Audio initialized successfully (ready for playback)');
  } catch (error) {
    console.warn("Could not initialize notification sound:", error);
  }
};

// Play notification sound
// Callers gate this on settings.browserNotificationsEnabled (and the relevant
// notifyOn* flags) before calling — there is no separate "sound" setting;
// sound follows the same on/off state as notifications.
export const playNotificationSound = async (settings) => {
  // Sound always plays - no need to check soundEnabled

  try {
    // Creates the element if missing, or swaps it if the account's sound changed
    ensureAudioElement();

    if (audioElement) {
      // Reset to start and play
      audioElement.currentTime = 0;

      // Try to play
      const playPromise = audioElement.play();

      if (playPromise !== undefined) {
        await playPromise.catch(error => {
          // Handle autoplay policy restrictions
          // If autoplay is blocked, try to initialize on next user interaction
          if (error.name === 'NotAllowedError' || error.name === 'NotSupportedError') {
            // Re-initialize on next user interaction
            if (typeof window !== 'undefined') {
              const initOnInteraction = () => {
                ensureAudioElement();
                // Try to play again after initialization
                if (audioElement) {
                  audioElement.play().catch(() => {});
                }
                window.removeEventListener('click', initOnInteraction);
                window.removeEventListener('touchstart', initOnInteraction);
                window.removeEventListener('mousedown', initOnInteraction);
              };
              window.addEventListener('click', initOnInteraction, { once: true });
              window.addEventListener('touchstart', initOnInteraction, { once: true });
              window.addEventListener('mousedown', initOnInteraction, { once: true });
            }
          }
        });
      }
    }
  } catch (error) {
    // Silent error handling - don't spam console
  }
};

// Request browser notification permission
export const requestNotificationPermission = async () => {
  if (!("Notification" in window)) {
    return false;
  }
  
  if (Notification.permission === "granted") {
    return true;
  }
  
  if (Notification.permission !== "denied") {
    const permission = await Notification.requestPermission();
    return permission === "granted";
  }
  
  return false;
};

// Show browser notification (WhatsApp-style)
export const showBrowserNotification = async (title, options, settings) => {
  if (!settings?.browserNotificationsEnabled) return;
  
  const hasPermission = await requestNotificationPermission();
  
  if (hasPermission) {
    try {
      // Get domain from window location
      const domain = window.location.hostname || 'clusterden.com';
      
      // Format notification like WhatsApp:
      // Title: Contact Name
      // Body: Message content
      // Tag: For grouping notifications
      const notification = new Notification(title, {
        body: options.body || "New message",
        icon: '/favicon.ico', // App icon
        badge: '/favicon.ico',
        tag: options.tag || 'clusterden-notification', // Group notifications by contact
        requireInteraction: false,
        silent: false, // Sound is handled separately
        // Add domain/context in the body if needed (browser shows domain automatically)
        data: {
          ...options.data,
          domain: domain,
          contactId: options.data?.contactId || null
        }
      });
      
      // Auto-close after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);
      
      // Handle click to focus window and navigate to chat
      notification.onclick = () => {
        window.focus();
        
        // Navigate to the chat if contactId is provided
        if (options.data?.contactId) {
          try {
            // Get user data from Redux state (not localStorage for security)
            const { store } = require('../redux/store');
            const user = store.getState()?.auth?.user;
            
            if (user) {
              const accountId = user.accountId;
              const userId = user._id || user.userId;
              
              if (accountId && userId) {
                const chatUrl = `/home/${accountId}/inbox/${userId}/chat/${options.data.contactId}`;
                window.location.href = chatUrl;
              }
            }
          } catch (error) {
            console.warn("Could not navigate to chat:", error);
          }
        }
        
        notification.close();
      };
    } catch (error) {
      console.warn("Could not show browser notification:", error);
    }
  }
};

// Combined notification function - shows browser notification
// Note: Sound is now handled separately in socketHandler to always play
export const notifyUser = async (title, body, settings, contactName = null, contactId = null) => {
  const notificationTitle = contactName || title;
  const notificationBody = body || "New message";
  
  // Show browser notification if enabled
  await showBrowserNotification(
    notificationTitle, 
    { 
      body: notificationBody,
      tag: contactId ? `contact-${contactId}` : 'general',
      data: { contactId }
    }, 
    settings
  );
};

/**
 * Task chat only: browser alert for incoming user messages.
 * Shown on any page / tab state when browser notifications are enabled (unlike inbox, which
 * suppresses when the inbox view is focused). Sound is played separately in socketHandler;
 * `silent: true` avoids the OS playing a second chime.
 */
export const showTaskChatBrowserNotification = async (title, body, settings, { taskId } = {}) => {
  if (!settings?.browserNotificationsEnabled) return;

  const hasPermission = await requestNotificationPermission();
  if (!hasPermission) return;

  try {
    const notification = new Notification(title || "Task chat", {
      body: body || "New message",
      icon: "/favicon.ico",
      badge: "/favicon.ico",
      tag: taskId ? `task-chat-${taskId}` : "task-chat-message",
      requireInteraction: false,
      silent: true,
      data: { kind: "taskChat", taskId: taskId || null },
    });

    const closeMs = 6000;
    const t = setTimeout(() => {
      try {
        notification.close();
      } catch (_) {}
    }, closeMs);

    notification.onclick = () => {
      clearTimeout(t);
      window.focus();
      if (taskId) {
        try {
          const { store } = require("../redux/store");
          const user = store.getState()?.auth?.user;
          const accountId = user?.accountId;
          if (accountId) {
            window.location.href = `/home/${accountId}/tasks`;
          }
        } catch (error) {
          console.warn("Could not navigate to tasks:", error);
        }
      }
      try {
        notification.close();
      } catch (_) {}
    };
  } catch (error) {
    console.warn("Could not show task chat browser notification:", error);
  }
};

